import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { SlideshowExpressStyleRecord } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Settings, Plus, Pencil, Archive, Loader2, Search, Image as ImageIcon, Upload, Type, FileJson, FileUp, Eye, CloudUpload, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import {
  SLIDESHOW_BUNDLED_FONTS,
  SLIDESHOW_TEXT_PLACEMENT_OPTIONS,
  parseSlideshowTextSettings,
  type SlideshowCustomFont,
} from "@shared/slideshow-text-settings";
import { useSlideshowPickerFonts } from "@/lib/slideshow-font-loader";

// ---- form state ----------------------------------------------------------

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

type FormState = {
  value: string;
  label: string;
  shortDescription: string;
  styleDescription: string;
  imageUrl: string;
  // Slideshow text settings — every "" / "default" / null means "leave this
  // knob on the style's built-in default" so untouched styles render as today.
  tFontKey: string;                       // "" = default, bundled key, or "custom"
  tCustomFont: SlideshowCustomFont | null;
  tColor: string;                         // "" = default, else #RRGGBB
  tSizePx: string;                        // "" = default, else int (1080-width px)
  tStroke: "default" | "on" | "off";
  tStrokeColor: string;
  tStrokeWidthPx: string;
  tAlign: "" | "left" | "center" | "right";
  tPlacement: "" | "upper" | "upperThird" | "center" | "belowCenter";
};

const EMPTY_FORM: FormState = {
  value: "", label: "", shortDescription: "", styleDescription: "", imageUrl: "",
  tFontKey: "", tCustomFont: null, tColor: "", tSizePx: "",
  tStroke: "default", tStrokeColor: "", tStrokeWidthPx: "",
  tAlign: "", tPlacement: "",
};

function styleToForm(s: SlideshowExpressStyleRecord): FormState {
  const ts = parseSlideshowTextSettings((s as any).textSettings);
  return {
    value: s.value ?? "",
    label: s.label ?? "",
    shortDescription: s.shortDescription ?? "",
    styleDescription: s.styleDescription ?? "",
    imageUrl: s.imageUrl ?? "",
    tFontKey: ts?.customFont ? "custom" : (ts?.fontKey ?? ""),
    tCustomFont: ts?.customFont ?? null,
    tColor: ts?.color ?? "",
    tSizePx: ts?.sizePx != null ? String(ts.sizePx) : "",
    tStroke: ts?.strokeEnabled === true ? "on" : ts?.strokeEnabled === false ? "off" : "default",
    tStrokeColor: ts?.strokeColor ?? "",
    tStrokeWidthPx: ts?.strokeWidthPx != null ? String(ts.strokeWidthPx) : "",
    tAlign: ts?.align ?? "",
    tPlacement: ts?.placement ?? "",
  };
}

// Build the textSettings blob from the form. Returns null when every knob is
// on "default" so the style keeps rendering with its hardcoded look.
function formToTextSettings(f: FormState): Record<string, unknown> | null {
  const ts: Record<string, unknown> = {};
  if (f.tFontKey === "custom" && f.tCustomFont) ts.customFont = f.tCustomFont;
  else if (f.tFontKey && f.tFontKey !== "custom") ts.fontKey = f.tFontKey;
  if (HEX_RE.test(f.tColor)) ts.color = f.tColor;
  const size = parseInt(f.tSizePx, 10);
  if (Number.isFinite(size) && size >= 16 && size <= 240) ts.sizePx = size;
  if (f.tStroke === "off") ts.strokeEnabled = false;
  if (f.tStroke === "on") {
    ts.strokeEnabled = true;
    if (HEX_RE.test(f.tStrokeColor)) ts.strokeColor = f.tStrokeColor;
    const w = parseFloat(f.tStrokeWidthPx);
    if (Number.isFinite(w) && w > 0 && w <= 40) ts.strokeWidthPx = w;
  }
  if (f.tAlign) ts.align = f.tAlign;
  if (f.tPlacement) ts.placement = f.tPlacement;
  return Object.keys(ts).length > 0 ? ts : null;
}

function formToPayload(f: FormState): Record<string, unknown> {
  const slug = (f.value.trim() || f.label.trim())
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .replace(/^_+|_+$/g, "");
  return {
    value: slug,
    label: f.label.trim(),
    shortDescription: f.shortDescription.trim() || null,
    styleDescription: f.styleDescription.trim() || null,
    imageUrl: f.imageUrl.trim() || null,
    textSettings: formToTextSettings(f),
  };
}

// ---- bulk JSON import ------------------------------------------------------

type ImportEntryResult = {
  index: number;
  value: string | null;
  label: string | null;
  status: "created" | "skipped" | "error";
  reason?: string;
};
type ImportResponse = {
  created: number;
  skipped: number;
  errored: number;
  results: ImportEntryResult[];
};

const IMPORT_EXAMPLE = `[
  {
    "value": "cozyautumn",
    "label": "Cozy Autumn",
    "shortDescription": "Warm, nostalgic fall montage",
    "styleDescription": "Describe the whole style here...",
    "imageUrl": "https://example.com/photo.webp",
    "sortOrder": 10,
    "textSettings": { "fontKey": "anton", "color": "#ffffff" }
  },
  {
    "label": "Candid Friend-Cam",
    "short_description": "snake_case keys work too",
    "style_description": "Describe the whole style here...",
    "picker_photo_url": "data:image/png;base64,iVBORw0KG..."
  }
]`;

// ---- field components -----------------------------------------------------

function TextField({ id, label, value, onChange, placeholder, mono }: {
  id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} placeholder={placeholder} className={mono ? "font-mono text-xs" : undefined}
        onChange={(e) => onChange(e.target.value)} data-testid={`input-${id}`} />
    </div>
  );
}

function AreaField({ id, label, value, onChange, hint, rows = 3 }: {
  id: string; label: string; value: string; onChange: (v: string) => void; hint?: string; rows?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <Textarea id={id} value={value} rows={rows} className="font-normal"
        onChange={(e) => onChange(e.target.value)} data-testid={`textarea-${id}`} />
    </div>
  );
}

// ---- manager -------------------------------------------------------------

export default function SlideshowStyleManager({ inline = false }: { inline?: boolean } = {}) {
  const { toast } = useToast();
  const [managerOpen, setManagerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SlideshowExpressStyleRecord | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [archiveTarget, setArchiveTarget] = useState<SlideshowExpressStyleRecord | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fontInputRef = useRef<HTMLInputElement>(null);

  // Bulk JSON import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importEntries, setImportEntries] = useState<unknown[] | null>(null);
  const [importFileName, setImportFileName] = useState<string>("");
  const [importFileSize, setImportFileSize] = useState<number>(0);
  const [importParseError, setImportParseError] = useState<string>("");
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [importWasDryRun, setImportWasDryRun] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Global query defaults (retry:false + staleTime:Infinity) would cache a
  // transient first-paint 401 forever and wedge this shared endpoint empty, so
  // override them: retry the auth race and re-fetch on mount.
  const { data: styles, isLoading } = useQuery<SlideshowExpressStyleRecord[]>({
    queryKey: ["/api/slideshow-express-styles"],
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
    enabled: inline || managerOpen,
  });

  const upd = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  // Load the real render fonts (bundled TTFs + any uploaded custom font) into
  // the browser once the edit dialog opens, so the font dropdown and the
  // preview box show each option in its actual typeface. Fail-open: while a
  // font is still loading the cssFallback stack stands in, then upgrades.
  const { bundledFamily, customFamily } = useSlideshowPickerFonts(
    form.tCustomFont,
    dialogOpen,
  );

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/slideshow-express-styles/upload-image", {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(msg.message || "Upload failed");
      }
      const data = await res.json();
      upd({ imageUrl: data.url });
      toast({ title: "Photo attached" });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFontUpload = async (file: File) => {
    setUploadingFont(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/slideshow-express-styles/upload-font", {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(msg.message || "Upload failed");
      }
      const data = await res.json();
      upd({ tCustomFont: { url: data.url, family: data.family, fileName: data.fileName ?? null }, tFontKey: "custom" });
      toast({ title: "Font uploaded", description: `Family: ${data.family}` });
    } catch (e: any) {
      toast({ title: "Font upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingFont(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      if (!payload.label) throw new Error("Label is required");
      if (!payload.value) throw new Error("Slug is required");
      if (editing) {
        return apiRequest("PATCH", `/api/slideshow-express-styles/${editing.id}`, payload);
      }
      return apiRequest("POST", "/api/slideshow-express-styles", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/slideshow-express-styles"] });
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      toast({ title: editing ? "Style updated" : "Style created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/slideshow-express-styles/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/slideshow-express-styles"] });
      setArchiveTarget(null);
      toast({ title: "Style deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleImportFile = (file: File) => {
    setImportResult(null);
    setImportParseError("");
    setImportEntries(null);
    setImportFileName(file.name);
    setImportFileSize(file.size);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const entries = Array.isArray(parsed) ? parsed : parsed?.entries;
        if (!Array.isArray(entries) || entries.length === 0) {
          setImportParseError("The file must contain a JSON array of style objects (or { \"entries\": [...] }).");
          return;
        }
        setImportEntries(entries);
      } catch (e: any) {
        setImportParseError(`Couldn't parse this file as JSON: ${e.message}`);
      }
    };
    reader.onerror = () => setImportParseError("Couldn't read the file.");
    reader.readAsText(file);
  };

  const importMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await apiRequest("POST", "/api/slideshow-express-styles/import", { entries: importEntries, dryRun });
      const data = (await res.json()) as ImportResponse;
      return { data, dryRun };
    },
    onSuccess: ({ data, dryRun }) => {
      setImportResult(data);
      setImportWasDryRun(dryRun);
      if (!dryRun) {
        setImportEntries(null);
        queryClient.invalidateQueries({ queryKey: ["/api/slideshow-express-styles"] });
      }
      toast({
        title: dryRun ? "Dry run complete" : "Import complete",
        description: `${data.created} ${dryRun ? "would be created" : "created"}, ${data.skipped} skipped, ${data.errored} failed.`,
        variant: data.errored > 0 && data.created === 0 ? "destructive" : undefined,
      });
    },
    onError: (e: Error) => toast({ title: "Bulk import failed", description: e.message, variant: "destructive" }),
  });

  const openImport = () => {
    setImportEntries(null);
    setImportFileName("");
    setImportFileSize(0);
    setImportParseError("");
    setImportResult(null);
    setImportWasDryRun(false);
    setImportOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (s: SlideshowExpressStyleRecord) => {
    setEditing(s);
    setForm(styleToForm(s));
    setDialogOpen(true);
  };

  const filtered = (styles ?? []).filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.label.toLowerCase().includes(q) ||
      (s.value ?? "").toLowerCase().includes(q) ||
      (s.shortDescription ?? "").toLowerCase().includes(q)
    );
  });

  // Shared list body — rendered inside the manager dialog (button mode) or
  // directly on the page (inline mode, used by the Admin Dashboard).
  const managerBody = (
    <>

          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search styles..." className="pl-9 h-9" data-testid="input-search-styles" />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={openImport} size="sm" variant="outline" data-testid="button-import-styles">
                <FileUp className="h-4 w-4 mr-2" /> Bulk Import
              </Button>
              <Button onClick={openCreate} size="sm" data-testid="button-new-style">
                <Plus className="h-4 w-4 mr-2" /> New style
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="text-empty">
              No styles yet. Create one to get started.
            </div>
          ) : (
            <div className="grid gap-2.5">
              {filtered.map((s) => (
                <Card key={s.id} data-testid={`card-manage-style-${s.id}`}>
                  <CardContent className="flex items-start justify-between gap-3 p-3">
                    <div className="flex items-start gap-3 min-w-0">
                      {s.imageUrl ? (
                        <img src={s.imageUrl} alt="" className="h-12 w-12 rounded-lg object-cover border border-border/50 shrink-0" data-testid={`img-manage-style-${s.id}`} />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-muted/50 border border-border/40 flex items-center justify-center shrink-0">
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="space-y-0.5 min-w-0">
                        <p className="text-sm font-semibold leading-tight" data-testid={`text-manage-style-label-${s.id}`}>{s.label}</p>
                        <p className="text-[11px] text-muted-foreground font-mono">{s.value}</p>
                        {s.shortDescription && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{s.shortDescription}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)}
                        data-testid={`button-edit-style-${s.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setArchiveTarget(s)}
                        data-testid={`button-delete-style-${s.id}`}>
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
    </>
  );

  return (
    <>
      {inline ? (
        <div className="space-y-4" data-testid="inline-slideshow-styles">
          <p className="text-sm text-muted-foreground">
            The global bank of narrative styles for Slideshow Express. Edits appear instantly in
            the picker; runs that already chose a style are unaffected.
          </p>
          {managerBody}
        </div>
      ) : (
        <>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setManagerOpen(true)}
            data-testid="button-manage-styles">
            <Settings className="h-3.5 w-3.5" /> Manage styles
          </Button>

          {/* Manager dialog — list + create/edit/delete */}
          <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
            <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Slideshow styles</DialogTitle>
                <DialogDescription>
                  The global bank of narrative styles for Slideshow Express. Edits appear instantly in
                  the picker; runs that already chose a style are unaffected.
                </DialogDescription>
              </DialogHeader>
              {managerBody}
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit style" : "New style"}</DialogTitle>
            <DialogDescription>
              This description steers every Sim agent for this style. Edits apply to new runs only —
              runs already in flight keep the style they were submitted with.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <TextField id="label" label="Label" value={form.label} onChange={(v) => upd({ label: v })}
              placeholder="Day in the life" />

            <div className="space-y-1.5">
              <Label>Picker photo (500×500)</Label>
              <p className="text-xs text-muted-foreground">Shown on this style's card in the picker. Any image works — it's cropped to a centered 500×500 square.</p>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-lg border border-border/60 bg-muted/40 overflow-hidden shrink-0 flex items-center justify-center">
                  {form.imageUrl ? (
                    <img src={form.imageUrl} alt="" className="h-full w-full object-cover" data-testid="img-photo-preview" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col items-start gap-1.5">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ""; }}
                    data-testid="input-photo" />
                  <Button type="button" variant="outline" size="sm" disabled={uploadingImage}
                    onClick={() => fileInputRef.current?.click()} data-testid="button-upload-photo">
                    {uploadingImage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {form.imageUrl ? "Replace photo" : "Upload photo"}
                  </Button>
                  {form.imageUrl && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-muted-foreground"
                      onClick={() => upd({ imageUrl: "" })} data-testid="button-remove-photo">Remove</Button>
                  )}
                </div>
              </div>
            </div>

            <TextField id="shortDescription" label="Short description / subtitle" value={form.shortDescription}
              onChange={(v) => upd({ shortDescription: v })} placeholder="A relatable morning-to-night montage" />
            <AreaField id="styleDescription" label="Style description" value={form.styleDescription}
              onChange={(v) => upd({ styleDescription: v })} rows={12}
              hint="The single free-text message that steers every Sim agent for this style. Describe the whole style in one place — what it is, its emotional arc, how it works with or without a product, how the imagery should look, any comment/DM bait ideas, and what to avoid." />

            {/* ---- Slideshow text (overlay) settings ---- */}
            <div className="space-y-3 rounded-lg border border-border/60 p-3">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-semibold">Slideshow text</p>
              </div>
              <p className="text-xs text-muted-foreground">
                How the on-slide copy is drawn onto this style's images. Anything left on
                "Style default" keeps this style's current built-in look, so existing styles
                render exactly as before until you change something. Size and outline width
                are in pixels on a 1080-wide slide.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Font</Label>
                  <Select
                    value={form.tFontKey || "default"}
                    onValueChange={(v) => upd({ tFontKey: v === "default" ? "" : v })}
                  >
                    <SelectTrigger
                      data-testid="select-text-font"
                      style={{
                        fontFamily:
                          form.tFontKey === "custom" && form.tCustomFont
                            ? customFamily()
                            : form.tFontKey
                              ? bundledFamily(form.tFontKey)
                              : undefined,
                      }}
                    >
                      <SelectValue placeholder="Style default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Style default</SelectItem>
                      {SLIDESHOW_BUNDLED_FONTS.map((f) => (
                        <SelectItem key={f.key} value={f.key} style={{ fontFamily: bundledFamily(f.key) }}>
                          {f.label}
                        </SelectItem>
                      ))}
                      {form.tCustomFont && (
                        <SelectItem value="custom" style={{ fontFamily: customFamily() }}>
                          Uploaded: {form.tCustomFont.family}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <input ref={fontInputRef} type="file" accept=".ttf,.otf,.woff" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFontUpload(f); e.target.value = ""; }}
                      data-testid="input-font-file" />
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" disabled={uploadingFont}
                      onClick={() => fontInputRef.current?.click()} data-testid="button-upload-font">
                      {uploadingFont ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                      {form.tCustomFont ? "Replace font" : "Upload font"}
                    </Button>
                    {form.tCustomFont && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground"
                        onClick={() => upd({ tCustomFont: null, tFontKey: form.tFontKey === "custom" ? "" : form.tFontKey })}
                        data-testid="button-remove-font">Remove</Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">TTF, OTF, or WOFF (max 5MB).</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="text-size">Size (px at 1080 width)</Label>
                  <Input id="text-size" type="number" min={16} max={240} value={form.tSizePx}
                    placeholder="Style default"
                    onChange={(e) => upd({ tSizePx: e.target.value })} data-testid="input-text-size" />
                  <Label htmlFor="text-color" className="pt-1 block">Text color</Label>
                  <div className="flex items-center gap-2">
                    <input id="text-color" type="color" value={HEX_RE.test(form.tColor) ? form.tColor : "#ffffff"}
                      className="h-9 w-10 rounded border border-border/60 bg-transparent p-0.5 cursor-pointer"
                      onChange={(e) => upd({ tColor: e.target.value })} data-testid="input-text-color" />
                    <Input value={form.tColor} placeholder="Style default"
                      className="font-mono text-xs"
                      onChange={(e) => upd({ tColor: e.target.value.trim() })} data-testid="input-text-color-hex" />
                    {form.tColor && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground shrink-0"
                        onClick={() => upd({ tColor: "" })} data-testid="button-clear-text-color">Clear</Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Outline</Label>
                  <Select value={form.tStroke} onValueChange={(v) => upd({ tStroke: v as FormState["tStroke"] })}>
                    <SelectTrigger data-testid="select-text-stroke">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Style default</SelectItem>
                      <SelectItem value="on">On</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Alignment</Label>
                  <Select value={form.tAlign || "default"}
                    onValueChange={(v) => upd({ tAlign: v === "default" ? "" : (v as FormState["tAlign"]) })}>
                    <SelectTrigger data-testid="select-text-align">
                      <SelectValue placeholder="Style default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Style default (centered)</SelectItem>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Vertical placement</Label>
                  <Select value={form.tPlacement || "default"}
                    onValueChange={(v) => upd({ tPlacement: v === "default" ? "" : (v as FormState["tPlacement"]) })}>
                    <SelectTrigger data-testid="select-text-placement">
                      <SelectValue placeholder="Style default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Style default</SelectItem>
                      {SLIDESHOW_TEXT_PLACEMENT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.tStroke === "on" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="stroke-color">Outline color</Label>
                    <div className="flex items-center gap-2">
                      <input id="stroke-color" type="color" value={HEX_RE.test(form.tStrokeColor) ? form.tStrokeColor : "#000000"}
                        className="h-9 w-10 rounded border border-border/60 bg-transparent p-0.5 cursor-pointer"
                        onChange={(e) => upd({ tStrokeColor: e.target.value })} data-testid="input-stroke-color" />
                      <Input value={form.tStrokeColor} placeholder="Auto contrast"
                        className="font-mono text-xs"
                        onChange={(e) => upd({ tStrokeColor: e.target.value.trim() })} data-testid="input-stroke-color-hex" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="stroke-width">Outline width (px at 1080 width)</Label>
                    <Input id="stroke-width" type="number" min={1} max={40} value={form.tStrokeWidthPx}
                      placeholder="Auto"
                      onChange={(e) => upd({ tStrokeWidthPx: e.target.value })} data-testid="input-stroke-width" />
                  </div>
                </div>
              )}

              {/* Live preview — renders in the real font the server uses (loaded
                  via /api/slideshow-fonts + the brand-font proxy), with the
                  cssFallback stack standing in only while a font is loading. */}
              <div className="space-y-1">
                <Label>Preview</Label>
                <div
                  className="rounded-lg overflow-hidden border border-border/60 flex px-[10%]"
                  style={{
                    height: 190,
                    background: "linear-gradient(160deg, #6d6a75 0%, #4a4652 45%, #2e2b35 100%)",
                    alignItems:
                      form.tPlacement === "upper" ? "flex-start" :
                      form.tPlacement === "upperThird" ? "flex-start" :
                      form.tPlacement === "belowCenter" ? "flex-end" : "center",
                    paddingTop: form.tPlacement === "upperThird" ? 44 : 12,
                    paddingBottom: form.tPlacement === "belowCenter" ? 28 : 12,
                  }}
                  data-testid="preview-text-settings"
                >
                  <p
                    className="w-full leading-snug"
                    style={{
                      color: HEX_RE.test(form.tColor) ? form.tColor : "#ffffff",
                      fontSize: Math.round(((parseInt(form.tSizePx, 10) || 58) / 1080) * 560),
                      fontFamily: form.tFontKey === "custom" && form.tCustomFont
                        ? customFamily()
                        : bundledFamily(form.tFontKey),
                      textAlign: (form.tAlign || "center") as any,
                      WebkitTextStroke: form.tStroke === "on"
                        ? `${Math.max(0.5, ((parseFloat(form.tStrokeWidthPx) || 4) / 1080) * 560 / 2)}px ${HEX_RE.test(form.tStrokeColor) ? form.tStrokeColor : "#000000"}`
                        : form.tStroke === "off" ? "0px transparent" : "1px rgba(0,0,0,0.55)",
                      textShadow: form.tStroke === "off" ? "none" : "0 2px 6px rgba(0,0,0,0.35)",
                    }}
                  >
                    Your slide text looks like this
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Shown in the actual render font. Exact size, wrapping, and outline are finalized on the server.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-style">Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save-style">
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk JSON import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5" />
              Bulk Import Slideshow Styles
            </DialogTitle>
            <DialogDescription>
              Upload a master JSON file (one entry per style). Existing rows are matched by{" "}
              <code>value</code> slug and skipped; new slugs are created. Use Dry Run to preview
              what would happen without changing anything.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="import-metadata">
                <span className="inline-flex items-center gap-1.5">
                  <FileJson className="h-4 w-4" /> Metadata JSON
                </span>
              </Label>
              <Input
                id="import-metadata"
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }}
                data-testid="input-import-file"
              />
              <p className="text-xs text-muted-foreground">
                Either an array <code>[...]</code> or an object <code>{`{ "entries": [...] }`}</code>.{" "}
                <span className="font-medium text-foreground">label</span> is required per entry; optional:{" "}
                <code>value</code>, <code>shortDescription</code>, <code>styleDescription</code>,{" "}
                <code>imageUrl</code>, <code>sortOrder</code>, <code>textSettings</code>.
                Snake_case spellings (<code>short_description</code>, <code>style_description</code>,{" "}
                <code>picker_photo_url</code> / <code>image_url</code>, <code>text_settings</code>,{" "}
                <code>sort_order</code>) also work. The picker photo can be a hosted URL or a base64{" "}
                <code>data:image/...</code> data URL — base64 photos are decoded and stored as the
                standard 500×500 picker image.
              </p>
              {importFileName && (
                <p className="text-xs" data-testid="text-import-filename">
                  {importFileName} · {(importFileSize / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <details className="rounded-md border bg-muted/30 p-3">
              <summary className="text-xs font-medium cursor-pointer">Example file</summary>
              <pre className="text-[11px] font-mono bg-background/80 rounded p-2 mt-2 overflow-x-auto" data-testid="text-import-example">{IMPORT_EXAMPLE}</pre>
            </details>

            {importParseError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive" data-testid="text-import-parse-error">
                {importParseError}
              </div>
            )}

            {importEntries && !importResult && (
              <div className="rounded-md border p-3 text-sm" data-testid="text-import-preview">
                Ready to import <span className="font-semibold">{importEntries.length}</span>{" "}
                style entr{importEntries.length === 1 ? "y" : "ies"}.
              </div>
            )}

            {importResult && (
              <div className="space-y-2" data-testid="section-import-results">
                {importWasDryRun && (
                  <p className="text-xs text-muted-foreground" data-testid="text-import-dryrun-note">
                    Dry run — nothing was saved. Click Import to apply.
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400" data-testid="text-import-created">
                    <CheckCircle2 className="h-4 w-4" /> {importResult.created} {importWasDryRun ? "would be created" : "created"}
                  </span>
                  <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400" data-testid="text-import-skipped">
                    <MinusCircle className="h-4 w-4" /> {importResult.skipped} skipped
                  </span>
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400" data-testid="text-import-errored">
                    <XCircle className="h-4 w-4" /> {importResult.errored} failed
                  </span>
                </div>
                <div className="rounded-md border divide-y max-h-64 overflow-y-auto">
                  {importResult.results.map((r) => (
                    <div key={r.index} className="flex items-start gap-2 p-2 text-xs" data-testid={`row-import-result-${r.index}`}>
                      {r.status === "created" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                      ) : r.status === "skipped" ? (
                        <MinusCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                      )}
                      <div className="min-w-0">
                        <span className="font-medium">
                          {r.label || r.value || `Entry ${r.index + 1}`}
                        </span>
                        {r.value && <span className="text-muted-foreground font-mono ml-1.5">{r.value}</span>}
                        {r.reason && <div className="text-muted-foreground mt-0.5">{r.reason}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2 sm:justify-start">
            <Button
              variant="outline"
              disabled={!importEntries || importMutation.isPending}
              onClick={() => importMutation.mutate(true)}
              data-testid="button-import-dry-run"
            >
              {importMutation.isPending && importMutation.variables === true ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-1.5" />
              )}
              Dry Run
            </Button>
            <Button
              disabled={!importEntries || importMutation.isPending}
              onClick={() => importMutation.mutate(false)}
              data-testid="button-run-import"
            >
              {importMutation.isPending && importMutation.variables === false ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4 mr-1.5" />
              )}
              Import
            </Button>
            <Button variant="ghost" onClick={() => setImportOpen(false)} data-testid="button-close-import">
              {importResult && !importWasDryRun ? "Close" : "Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete style?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveTarget?.label}" will be removed from the picker. Runs that already chose it
              keep working — they still resolve to coherent guidance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
              data-testid="button-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
